import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { ProgressBar, Table } from "@opendate-app/setlist";

import client from "../../../shared/api/client";

const compact = (n) => Number(n || 0).toLocaleString("en-US", { notation: "compact", maximumFractionDigits: 1 });
const money = (n) => `$${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
const moneyCompact = (n) => `$${compact(n)}`;
const plural = (count, word) => `${count.toLocaleString("en-US")} ${word}${count === 1 ? "" : "s"}`;
const sum = (rows, key) => rows.reduce((total, row) => total + Number(row[key] || 0), 0);

const daysUntil = (startTime) => {
  if (!startTime) return null;
  const days = Math.ceil((new Date(startTime) - Date.now()) / 86400000);
  return days >= 0 ? days : null;
};

const Stat = ({ label, value }) => (
  <div className="marketing-summary__stat">
    <strong>{value}</strong>
    <span>{label}</span>
  </div>
);

Stat.propTypes = { label: PropTypes.string.isRequired, value: PropTypes.node.isRequired };

const messageRow = (key, channel, anchor, payload) => {
  const totals = payload?.totals || {};
  return {
    key,
    channel,
    anchor,
    activity: `${plural(payload?.rows?.length || 0, "send")} · ${(totals.sent || 0).toLocaleString("en-US")} recipients`,
    orders: totals.attributed_orders || 0,
    revenue: totals.attributed_revenue || 0,
  };
};

export const channelRows = ({ messages, sources, metaAdSpend, metaAdsConnected }) => {
  const siteRows = sources?.referring_site_rows || [];
  const referrals = siteRows.filter((r) => r.source_key !== "direct" && r.orders > 0);
  const direct = siteRows.find((r) => r.source_key === "direct");
  const rows = [
    messageRow("email", "Email", "email-marketing", messages?.email),
    messageRow("sms", "SMS", "sms-marketing", messages?.sms),
  ];

  if (metaAdsConnected) {
    const paid = sources?.paid_rows || [];
    const campaigns = new Set(metaAdSpend.map((ad) => ad.campaign_id).filter(Boolean)).size;
    rows.push({
      key: "meta",
      channel: "Meta Ads",
      anchor: "paid-sources",
      activity: `${plural(campaigns, "campaign")} · ${money(sum(metaAdSpend, "total_spend"))} spent · ${compact(sum(metaAdSpend, "total_impressions"))} impr.`,
      orders: sum(paid, "orders"),
      revenue: sum(paid, "revenue"),
    });
  }

  rows.push({
    key: "referral",
    channel: "Referral",
    anchor: "referring-sites",
    activity: referrals.map((r) => r.display_name).join(", ") || "None yet",
    orders: sum(referrals, "orders"),
    revenue: sum(referrals, "revenue"),
  });
  rows.push({
    key: "direct",
    channel: "Direct / website",
    anchor: "referring-sites",
    activity: "Venue site, typed URL",
    orders: direct?.orders || 0,
    revenue: direct?.revenue || 0,
  });

  return rows;
};

const MarketingSummary = ({ teamId, confirm, metaAdSpend, metaAdsConnected }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    const base = `/teams/${teamId}/confirms/${confirm.id}/marketing_insights`;
    const get = (path, params) => client.get(`${base}/${path}`, { params, signal: controller.signal }).then((r) => r.data);

    Promise.all([get("traffic"), get("sources", { range: "event" }), get("event_messages")])
      .then(([traffic, sources, messages]) => setData({ traffic, sources, messages }))
      .catch((err) => {
        if (err.name !== "CanceledError" && err.name !== "AbortError") setError(true);
      });
    return () => controller.abort();
  }, [teamId, confirm.id]);

  if (error) return <div role="alert">Couldn't load marketing. Refresh to try again.</div>;
  if (!data) return <Table columns={[]} data={[]} loading loadingLabel="Loading marketing…" />;

  const kpis = data.traffic.kpis;
  const capacity = kpis.capacity || 0;
  const sold = kpis.tickets_sold || 0;
  const forecast = kpis.forecast_data_sufficient ? Math.max(kpis.forecast || 0, sold) : null;
  const pct = (n) => (capacity > 0 ? (n / capacity) * 100 : 0);
  const days = daysUntil(confirm.start_time);
  const rows = channelRows({ messages: data.messages, sources: data.sources, metaAdSpend, metaAdsConnected });
  const gross = Number(kpis.gross || 0);
  const orderRevenue = Number(data.sources.total_revenue || 0);
  const pageViews = data.traffic.views_conversion?.totals || {};
  const insightsPath = `/teams/${teamId}/confirms/${confirm.id}/marketing_insights`;

  const columns = [
    {
      key: "channel",
      header: "Channel",
      primary: true,
      render: (r) => <a className="marketing-summary__channel" href={`${insightsPath}#${r.anchor}`}>{r.channel}</a>,
    },
    { key: "activity", header: "Activity", render: (r) => r.activity },
    { key: "orders", header: "Orders", align: "right", render: (r) => <strong>{r.orders.toLocaleString("en-US")}</strong> },
    { key: "revenue", header: "Revenue", align: "right", render: (r) => money(r.revenue) },
    {
      key: "share",
      header: "Share of revenue",
      width: "30%",
      render: (r) => <ProgressBar value={orderRevenue > 0 ? (r.revenue / orderRevenue) * 100 : 0} label="right" />,
    },
  ];

  return (
    <div className="marketing-summary">
      <div className="marketing-summary__header">
        <p className="marketing-summary__forecast">
          {forecast != null ? (
            <>Forecast <strong>~{forecast.toLocaleString("en-US")} of {capacity.toLocaleString("en-US")}</strong></>
          ) : (
            <><strong>{sold.toLocaleString("en-US")} of {capacity.toLocaleString("en-US")}</strong> sold</>
          )}
          {days != null ? ` · ${plural(days, "day")} to show` : null}
        </p>
        <div className="marketing-summary__metrics">
          <Stat label="Gross" value={moneyCompact(gross)} />
          {days != null ? (
            <Stat label="Tix / wk" value={(kpis.weekly_pace || 0).toLocaleString("en-US")} />
          ) : (
            <Stat label="Sell-through" value={`${Math.round(kpis.sell_through_pct || 0)}%`} />
          )}
          {pageViews.views > 0 ? <Stat label="Page conv." value={`${pageViews.conversion_rate}%`} /> : null}
        </div>
      </div>

      <div
        className="marketing-summary__bar"
        role="img"
        aria-label={`${sold} of ${capacity} sold${forecast != null ? `, ${forecast} forecast` : ""}`}
      >
        {forecast != null ? <span className="marketing-summary__bar-forecast" style={{ width: `${pct(forecast)}%` }} /> : null}
        <span className="marketing-summary__bar-sold" style={{ width: `${pct(sold)}%` }} />
      </div>
      <ul className="marketing-summary__legend">
        <li><i className="is-sold" /><strong>{sold.toLocaleString("en-US")} sold</strong> · {Math.round(pct(sold))}%</li>
        {forecast != null && forecast > sold ? (
          <li><i className="is-forecast" />~{(forecast - sold).toLocaleString("en-US")} more forecast</li>
        ) : null}
        {forecast != null && capacity > forecast ? (
          <li><i className="is-risk" />~{(capacity - forecast).toLocaleString("en-US")} at risk of not selling</li>
        ) : null}
      </ul>

      <Table columns={columns} data={rows} getRowKey={(r) => r.key} />

      <div className="marketing-summary__footer">
        <span>Email &amp; SMS credit this event's messages opened or clicked in the 7 days before purchase · Referral, Meta and direct use each order's source · An order can count in more than one row</span>
        <a href={insightsPath}>Open Marketing tab →</a>
      </div>
    </div>
  );
};

MarketingSummary.propTypes = {
  teamId: PropTypes.number.isRequired,
  confirm: PropTypes.object.isRequired,
  metaAdSpend: PropTypes.array,
  metaAdsConnected: PropTypes.bool,
};

MarketingSummary.defaultProps = { metaAdSpend: [], metaAdsConnected: false };

export default MarketingSummary;
